<template>
	<div class="supply-progress">
		<div class="supply-progress__labels">
			<Text class="supply-progress__count" font="--text-14-20-med" color="--text-dark-primary">
				{{ paid }}
				<span class="supply-progress__of">/ {{ total }} paid</span>
			</Text>
			<slot name="aside">
				<Text font="--text-14-20-med" :color="KOVA.textSecondary">{{ total - paid }} remaining</Text>
			</slot>
		</div>
		<div class="supply-progress__bars" :class="`supply-progress__bars--${tone}`" aria-hidden="true">
			<span
				v-for="i in total"
				:key="i"
				class="supply-progress__bar"
				:class="{ 'supply-progress__bar--on': i <= paid }"
			/>
		</div>
	</div>
</template>

<script setup lang="ts">
// "1 / 3 paid" + a segmented bar — payments made inside the current 3-month supply
// (`commitmentQuotes().progress`: the payments made, over `paymentsPerSupply` — the cycle's payments per
// supply of `supply_schedule`).
//
// Two places use it: the purple balance card on /cancel/commitment/options (right label "2
// remaining", the default `aside`) and the current supply row of the /status commitment card
// (`tone="brand"`, right label "Next charge: …" through the `aside` slot).
import { Text } from '@simple-life-app/growth-ui-kova'
import { KOVA } from '@/designTokens'

withDefaults(
	defineProps<{
		paid: number
		total: number
		/** Fill of the paid segments: purple on the balance card, Kova green on /status. */
		tone?: 'violet' | 'brand'
	}>(),
	{ tone: 'violet' },
)
</script>

<style scoped>
.supply-progress {
	display: flex;
	flex-direction: column;
	gap: var(--size-4);
	width: 100%;
}

.supply-progress__labels {
	display: flex;
	justify-content: space-between;
	align-items: flex-end;
	gap: var(--size-8);
}

/* Figma: the count is primary, "/ 3 paid" secondary (`text/black/secondary`, navy 55% — the DS
   secondary is 60%) — same font. `Text` paints its own colour inline, so the secondary half is a plain
   span inside it with its own colour. Since growth-ui-kova 0.58 DS Text paints a bare <span> only when
   the Text opts in with `--text-span-color` in its inline style, so this scoped rule wins. */
.supply-progress__of {
	color: rgba(50, 54, 83, 0.55);
}

/* 4 under the numbers (Figma 7415:20711 / 7415:17805: the block's gap, nothing more). */
.supply-progress__bars {
	display: flex;
	gap: var(--size-4);
}

.supply-progress__bar {
	flex: 1;
	height: 4px;
	border-radius: 2px;
	background: var(--stroke-dark-low);
}

/* The balance card's bar (Figma 7415:17805): `brand/symptoms/symptoms-primary` on a navy 25% track. */
.supply-progress__bars--violet .supply-progress__bar {
	background: rgba(50, 54, 83, 0.25);
}

.supply-progress__bars--violet .supply-progress__bar--on {
	background: #bc46b2;
}

.supply-progress__bars--brand .supply-progress__bar {
	/* `background/surface-on-primary` — the warm grey track of the /status bar (Figma 7415:20726). */
	background: #e7e5e3;
}

.supply-progress__bars--brand .supply-progress__bar--on {
	background: var(--background-brand-high);
}
</style>
